// src/components/chat/MessageBubble.jsx
// Renders a single chat message bubble including team invite interactive elements.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { tinyWhen, extractUidFromMxid, buildInviteStatusMessage } from './chatHelpers';

const QUICK_EMOJIS = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F44E}'];

// Simple URL regex to detect links in message text
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/g;

// Client-side cache so re-renders don't re-fetch
const previewCache = new Map();

/**
 * Turns plain text into JSX with clickable <a> links for any URLs found.
 */
function renderBodyWithLinks(text) {
  if (!text) return null;
  const regex = new RegExp(URL_REGEX.source, 'gi');
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const href = match[0];
    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#00aef7] underline hover:text-[#0090cc] break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {href}
      </a>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

/**
 * Like renderBodyWithLinks but also highlights @mentions for team rooms.
 * memberNames is an array of display names to match against.
 * memberMap is a Map<displayName, userId> for clickable mentions.
 * onMentionClick is called with userId when a mention is clicked.
 */
function renderBodyWithMentions(text, memberNames, memberMap, onMentionClick) {
  if (!text) return null;
  if (!Array.isArray(memberNames) || memberNames.length === 0) return renderBodyWithLinks(text);

  // Build a regex that matches URLs or @MemberName patterns
  // Sort member names longest-first so "John Smith" matches before "John"
  const sorted = [...memberNames].sort((a, b) => b.length - a.length);
  const escapedNames = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const mentionPattern = escapedNames.map((n) => `@${n}`).join('|');
  const combinedRegex = new RegExp(`(${URL_REGEX.source})|(${mentionPattern})`, 'gi');

  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      // URL match
      const href = match[1];
      parts.push(
        <a
          key={`url-${match.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#00aef7] underline hover:text-[#0090cc] break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {href}
        </a>
      );
    } else {
      // @mention match — clickable if we have a userId mapping
      const mentionText = match[0];
      const mentionName = mentionText.slice(1); // strip leading '@'
      const mentionUid = memberMap ? memberMap.get(mentionName) : null;

      if (mentionUid && onMentionClick) {
        parts.push(
          <button
            key={`mention-${match.index}`}
            type="button"
            className="text-[#00aef7] font-semibold cursor-pointer hover:underline inline bg-transparent border-0 p-0 m-0 text-sm font-inherit"
            onClick={(e) => {
              e.stopPropagation();
              onMentionClick(mentionUid);
            }}
          >
            {mentionText}
          </button>
        );
      } else {
        parts.push(
          <span key={`mention-${match.index}`} className="text-[#00aef7] font-semibold">
            {mentionText}
          </span>
        );
      }
    }
    lastIndex = combinedRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function LinkPreviewCard({ url }) {
  const [preview, setPreview] = useState(previewCache.get(url) || null);
  const [loading, setLoading] = useState(!previewCache.has(url));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (previewCache.has(url)) {
      setPreview(previewCache.get(url));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch('/api/chat/link-preview', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.ok && data.data) {
          previewCache.set(url, data.data);
          setPreview(data.data);
        } else {
          setFailed(true);
        }
      })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [url]);

  // Don't show anything until we have actual preview data (no loading skeleton)
  if (failed || loading || !preview) return null;

  return (
    <a
      href={preview.url || url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors flex overflow-hidden no-underline cursor-pointer block"
      onClick={(e) => e.stopPropagation()}
    >
      {preview.image && (
        <div className="w-20 h-20 flex-shrink-0 bg-gray-100">
          <img
            src={preview.image}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <div className="flex-1 p-2 min-w-0">
        <div className="text-xs font-semibold text-gray-900 truncate">
          {preview.title}
        </div>
        {preview.description && (
          <div className="text-[11px] text-gray-500 line-clamp-2 mt-0.5 leading-tight">
            {preview.description}
          </div>
        )}
        <div className="text-[10px] text-gray-400 mt-1 truncate">
          {preview.domain || preview.siteName || ''}
        </div>
      </div>
    </a>
  );
}

export default function MessageBubble({
  message,
  isMine,
  isTeamRoom,
  isLastSeen,
  reactions,
  myMxid,
  onReact,
  onRemoveReact,
  onReply,
  onEdit,
  onDelete,
  replyToMessage,
  onScrollToMessage,
  myTeams,
  mxClient,
  getOtherUser,
  openTeam,
  setTeamInviteStatusModal,
  roomMemberNames,
  roomMemberMap,
  onMentionClick,
}) {
  const m = message;
  const bodyText = m.body || '';
  const isTeamInvite =
    typeof bodyText === 'string' && bodyText.startsWith('__EH_TEAM_INVITE__::');
  const isSystemMsg = !!m.system;

  // Hover state for the quick-react picker
  const [showPicker, setShowPicker] = useState(false);
  const hideTimerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    setShowPicker(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hideTimerRef.current = setTimeout(() => setShowPicker(false), 300);
  }, []);

  // Team room sender name header
  const senderHeader = isTeamRoom && !isSystemMsg ? (
    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
      <span className="truncate">
        {isMine
          ? 'You'
          : (() => {
              const mxName = mxClient?.getUser?.(m.sender)?.displayName || '';
              const uid = extractUidFromMxid(m.sender);
              const storeName = uid ? getOtherUser(uid)?.displayName : null;
              const useMx = mxName && !mxName.startsWith('@');
              return (useMx ? mxName : null) || storeName || m.sender || '';
            })()}
      </span>
      <span className="ml-3 whitespace-nowrap">
        {tinyWhen(m.ts || m.origin_server_ts || m.timestamp || m.created_at || Date.now())}
      </span>
    </div>
  ) : null;

  // DM timestamp (1:1 only)
  const dmTimestamp = !isTeamRoom ? (
    <span className="text-[10px] text-gray-500 block text-right mt-1">
      {(() => {
        const raw = m.ts ?? m.origin_server_ts ?? m.timestamp ?? m.created_at ?? null;
        if (!raw) return '';
        const ms = typeof raw === 'string' ? Date.parse(raw) : Number(raw);
        if (!Number.isFinite(ms)) return '';
        const d = new Date(ms);
        const date = d.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        });
        const time = d.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        return `${date} - ${time.toLowerCase()}`;
      })()}
    </span>
  ) : null;

  // Render team invite content
  const renderTeamInvite = () => {
    let payload = null;
    try {
      const jsonStr = bodyText.split('__EH_TEAM_INVITE__::')[1] || '';
      payload = JSON.parse(jsonStr);
    } catch {
      payload = null;
    }

    const teamId = payload?.team_id != null ? Number(payload.team_id) : null;
    const payloadTeamName =
      payload?.team_name && String(payload.team_name).trim()
        ? String(payload.team_name).trim()
        : null;
    const hydratedTeamName =
      teamId && Array.isArray(myTeams)
        ? (myTeams.find((t) => Number(t.team_id) === teamId)?.team_name || null)
        : null;
    const safeTeamName = payloadTeamName || hydratedTeamName || `Team #${teamId || ''}`.trim();

    // Inviter view — no clickable button
    if (isMine) {
      return (
        <div className="w-full">
          <div className="rounded-2xl px-4 py-3 max-w-[70%] bg-green-50 border border-green-200">
            <p className="text-sm text-green-900 font-semibold">
              Your team invitation has been sent.
            </p>
            <p className="mt-1 text-sm text-green-900">
              Team &quot;{safeTeamName}&quot;.
            </p>
          </div>
        </div>
      );
    }

    // Invitee view — clickable "Join team" button
    return (
      <div className="w-full">
        <div className="rounded-2xl px-4 py-3 max-w-[70%] bg-green-50 border border-green-200">
          <button
            type="button"
            className="w-full rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2"
            onClick={async () => {
              const invite_token = payload?.invite_token ?? payload?.token ?? null;
              const invitee_u_id = payload?.invitee_u_id ?? null;

              if (!invite_token || !invitee_u_id) {
                alert('This invitation is missing required information and cannot be opened.');
                return;
              }

              try {
                const res = await fetch('/api/teams/invite/status', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    invite_token,
                    invitee_u_id: Number(invitee_u_id),
                  }),
                });

                const data = await res.json().catch(() => null);

                if (!res.ok || !data || data.ok !== true) {
                  setTeamInviteStatusModal({
                    open: true,
                    message: 'Unable to verify invitation status. Please try again.',
                  });
                  return;
                }

                if (!data.can_open) {
                  setTeamInviteStatusModal({
                    open: true,
                    message: buildInviteStatusMessage(data),
                  });
                  return;
                }

                try {
                  localStorage.setItem(
                    'eh_team_invite_banner',
                    JSON.stringify({
                      mode: 'incoming',
                      team_id: payload?.team_id ?? null,
                      team_name: payload?.team_name ?? safeTeamName ?? null,
                      invite_token,
                      u_id_inviter: payload?.u_id_inviter ?? payload?.inviter_u_id ?? null,
                      invitee_u_id: Number(invitee_u_id),
                      invitee_name: payload?.invitee_name ?? null,
                      expires_at: payload?.expires_at ?? null,
                    })
                  );
                } catch {
                  // ignore storage issues
                }

                openTeam(payload?.team_id);
              } catch (err) {
                console.error('[MessageBubble] invite/status check failed', err);
                setTeamInviteStatusModal({
                  open: true,
                  message: 'Unable to verify invitation status. Please try again.',
                });
              }
            }}
          >
            Join team {safeTeamName}!
          </button>

          <p className="mt-2 text-sm text-green-900">
            You&apos;ve been invited to join team &quot;{safeTeamName}&quot;. This invitation will expire 10 days from today.
          </p>
        </div>
      </div>
    );
  };

  // Build reaction badges from the reactions prop
  // reactions shape: { [emoji]: [{ userId, reactionEventId }] }
  const reactionEntries = reactions ? Object.entries(reactions) : [];
  const hasReactions = reactionEntries.length > 0;

  const handleReactionBadgeClick = (emoji) => {
    if (!reactions || !myMxid) return;
    const list = reactions[emoji] || [];
    const myReaction = list.find((r) => r.userId === myMxid);

    if (myReaction) {
      // Already reacted — remove it
      onRemoveReact?.(myReaction.reactionEventId);
    } else {
      // Add reaction
      onReact?.(emoji);
    }
  };

  // Don't show reaction picker or reply on system messages
  const canReact = !isSystemMsg && !m.deleted && !!m.eventId && !!onReact;
  const canReply = !isSystemMsg && !m.deleted && !!m.eventId && !!onReply && !isTeamInvite;
  const canEdit = !isSystemMsg && !m.deleted && isMine && !!m.eventId && !!onEdit && !isTeamInvite;
  const canDelete = !isSystemMsg && !m.deleted && isMine && !!m.eventId && !!onDelete && !isTeamInvite;

  // Detect first URL in message text for link preview
  const firstUrl = (!isTeamInvite && !isSystemMsg && bodyText)
    ? (bodyText.match(URL_REGEX) || [])[0] || null
    : null;

  return (
    <div
      className={isMine ? 'mb-3 flex flex-col items-end' : 'mb-3 flex flex-col items-start'}
      onMouseEnter={(canReact || canReply || canEdit || canDelete) ? handleMouseEnter : undefined}
      onMouseLeave={(canReact || canReply || canEdit || canDelete) ? handleMouseLeave : undefined}
    >
      {/* Bubble + floating picker wrapper */}
      <div className="relative max-w-[70%]">
        {/* Floating quick-react + reply picker */}
        {showPicker && (canReact || canReply || canEdit || canDelete) && (
          <div
            className={
              'absolute -top-8 z-10 flex gap-0.5 bg-white border border-gray-200 rounded-full shadow-lg px-1 py-0.5 ' +
              (isMine ? 'right-0' : 'left-0')
            }
          >
            {canReply && (
              <button
                type="button"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-xs transition-transform hover:scale-110"
                title="Reply"
                onClick={(e) => {
                  e.stopPropagation();
                  onReply?.(m);
                  setShowPicker(false);
                }}
              >
                {'\u21A9'}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-xs transition-transform hover:scale-110"
                title="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(m);
                  setShowPicker(false);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-xs text-red-400 hover:text-red-600 transition-transform hover:scale-110"
                title="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(m);
                  setShowPicker(false);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            )}
            {canReact && QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-sm transition-transform hover:scale-125"
                onClick={(e) => {
                  e.stopPropagation();
                  onReact?.(emoji);
                  setShowPicker(false);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        <div
          className={
            'rounded-2xl px-4 py-2 ' +
            (isSystemMsg
              ? 'bg-white border border-gray-200'
              : isMine
              ? 'bg-[#00aef7]/10 border border-[#00aef7]/40'
              : 'bg-yellow-50 border border-yellow-200')
          }
        >
          {senderHeader}

          {/* Reply context — quoted original message (clickable to scroll to original) */}
          {replyToMessage && !isTeamInvite && (
            <button
              type="button"
              className="mb-1.5 pl-2 border-l-2 border-[#00aef7]/50 rounded-sm text-left w-full hover:bg-black/5 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onScrollToMessage?.(replyToMessage.eventId);
              }}
            >
              <div className="text-[10px] text-[#00aef7] font-medium truncate">
                {replyToMessage.sender === myMxid ? 'You' : (
                  (() => {
                    const mxName = mxClient?.getUser?.(replyToMessage.sender)?.displayName || '';
                    const uid = extractUidFromMxid(replyToMessage.sender);
                    const storeName = uid ? getOtherUser(uid)?.displayName : null;
                    const useMx = mxName && !mxName.startsWith('@');
                    return (useMx ? mxName : null) || storeName || replyToMessage.sender || '';
                  })()
                )}
              </div>
              <p className="text-xs text-gray-500 line-clamp-2 whitespace-pre-wrap">
                {(replyToMessage.body || '').slice(0, 150)}
              </p>
            </button>
          )}

          {m.deleted ? (
            <p className="text-sm italic text-gray-400">This message was deleted</p>
          ) : isTeamInvite ? renderTeamInvite() : (
            <>
              <p className="text-sm whitespace-pre-wrap break-words">
                {isTeamRoom && Array.isArray(roomMemberNames) && roomMemberNames.length > 0
                  ? renderBodyWithMentions(bodyText, roomMemberNames, roomMemberMap, onMentionClick)
                  : renderBodyWithLinks(bodyText)}
              </p>
              {m.edited && (
                <span className="text-[10px] text-gray-400 italic">(edited)</span>
              )}
            </>
          )}

          {!m.deleted && firstUrl && <LinkPreviewCard url={firstUrl} />}

          {dmTimestamp}
        </div>
      </div>

      {/* Reaction badges */}
      {hasReactions && (
        <div className={'flex flex-wrap gap-1 mt-1 ' + (isMine ? 'mr-1' : 'ml-1')}>
          {reactionEntries.map(([emoji, list]) => {
            const iReacted = myMxid && list.some((r) => r.userId === myMxid);
            return (
              <button
                key={emoji}
                type="button"
                onClick={() => handleReactionBadgeClick(emoji)}
                className={
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-colors ' +
                  (iReacted
                    ? 'bg-[#00aef7]/10 border-[#00aef7]/40 text-[#00aef7]'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100')
                }
              >
                <span>{emoji}</span>
                {list.length > 1 && <span>{list.length}</span>}
              </button>
            );
          })}
        </div>
      )}

      {isLastSeen && isMine && (
        <span className="text-[10px] text-[#00aef7] mt-0.5 mr-1">
          Seen
        </span>
      )}
    </div>
  );
}
